/**
 * 採用担当者向けレポート生成
 * スコアの3×3組み合わせから9類型プロファイルを判定し、詳細所見テキストを返す。
 */

export interface ReportInput {
  riskPct: number | null;
  trustPct: number | null;
  adaptPct: number | null;
  respondentId: string;
  assessedAt: string;
}

export interface ReportOutput {
  profileName: string;
  profileCatch: string;
  summary: string;
  traits: string[];
  suitableRoles: string[];
  managementNote: string;
  adaptNote: string;
  riskDetail: string;
  trustDetail: string;
  disclaimer: string;
}

type ThreeKey = 'high' | 'mid' | 'low';

function toThreeKey(pct: number): ThreeKey {
  if (pct >= 60) return 'high';
  if (pct >= 40) return 'mid';
  return 'low';
}

interface ProfileDef {
  name: string;
  catch: string;
  summary: string;
  traits: string[];
  suitableRoles: string[];
  managementNote: string;
}

const PROFILES: Record<ThreeKey, Record<ThreeKey, ProfileDef>> = {
  high: {
    high: {
      name: '変革推進型',
      catch: '人を巻き込み、不確実性を推進力に変える',
      summary:
        'リスク許容度と対人信頼の両方が高く、変化の大きな環境で自ら旗を立てチームを牽引する力があります。新規事業や組織変革など、前例のない課題に対して特に高いパフォーマンスを発揮しやすい類型です。',
      traits: [
        '不確実な状況でも積極的に意思決定を行い、先行投資を恐れない',
        '相手への信頼をベースに協力関係を構築し、チームの連帯感を高める',
        '変化をリスクではなく機会として捉え、組織に挑戦文化をもたらす',
        '多様なステークホルダーを巻き込んだ推進力を持つ',
      ],
      suitableRoles: ['事業開発・新規事業推進', '新規営業・アカウントエグゼクティブ', 'プロジェクトマネジャー・プログラムオーナー'],
      managementNote:
        'エネルギーと推進力を活かすために裁量の大きな役割が適しています。一方、過度なリスクテイクを防ぐため、意思決定の検証フローや定期的な振り返りの仕組みを整えることで力が最大化されます。',
    },
    mid: {
      name: '挑戦主導型',
      catch: '自己主導で高い目標を追いかける実行者',
      summary:
        'リスク許容度が高く、対人信頼は状況に応じて柔軟に調整するバランスを持ちます。明確な目標と適切な裁量を与えると力を発揮しやすく、成果志向の環境で高いパフォーマンスを示す傾向があります。',
      traits: [
        '困難な目標や高いハードルを好み、自ら挑戦を設定する',
        '状況に応じて信頼と独立判断を使い分ける柔軟性がある',
        '結果にコミットし、自主的な問題解決を得意とする',
        '新規課題や変化の多い環境でモチベーションが高まりやすい',
      ],
      suitableRoles: ['営業・ビジネスデベロップメント', 'プロダクトオーナー・プロジェクトリード', 'スタートアップ・成長期のポジション全般'],
      managementNote:
        '自律性と目標の明確さがパフォーマンスの鍵です。細かい監督よりも、成果目標を明示した上で実行に委ねるマネジメントが効果的。チーム協働の場面では、連携の重要性を定期的に確認することが有効です。',
    },
    low: {
      name: '独立開拓型',
      catch: '独自判断で未踏の領域を切り開く',
      summary:
        'リスク許容度が高い一方で、他者への依存を最小化した独立したスタイルを持ちます。専門性や独自の視点を活かし、自律的に課題を解決する能力が高い類型です。',
      traits: [
        '他者の承認に依存せず、独自の判断で行動する自律性が高い',
        '高いリスク許容度を背景に、新しい領域への参入を厭わない',
        '競争的・独立的な環境でも動じない精神的タフネスがある',
        '専門的な課題に深く没頭し、成果を出す集中力がある',
      ],
      suitableRoles: ['研究開発・R&D職', 'テクニカルスペシャリスト・エキスパート職', '創業者・スタートアップ的ポジション'],
      managementNote:
        '独立性の高さを活かすため、成果目標の明確化と実行の自由度を確保することが重要です。チームとの協調が求められる場面では、情報共有のプロセスを明示的に設計し、コラボレーションの機会を定期的に設けることが推奨されます。',
    },
  },
  mid: {
    high: {
      name: '協調実行型',
      catch: '信頼関係を基盤に、着実に成果を積み上げる',
      summary:
        '対人信頼が高く、リスクへの判断はバランスがとれています。人との関係性を大切にしながら確実に成果を出すスタイルで、チームの安定性と協働文化の形成に貢献しやすい類型です。',
      traits: [
        '良好な対人関係を自然に構築し、チームの連帯感を高める',
        '状況に応じてリスクを柔軟に判断し、着実な実行力を発揮する',
        'コミュニケーションを重視し、関係者との信頼蓄積を得意とする',
        '長期的な関係構築の中で力を発揮するスタイルを持つ',
      ],
      suitableRoles: ['チームリーダー・マネジャー', 'カスタマーサクセス・アカウントマネジメント', 'HR・人材開発'],
      managementNote:
        '安定した成果と良好な職場環境への貢献が期待できます。急激な変化が少ない環境でより力を発揮しますが、変革期には変化の意味と目的を丁寧に伝えることでスムーズな適応を促せます。',
    },
    mid: {
      name: 'バランス適応型',
      catch: '状況を読みながら柔軟に動くオールラウンダー',
      summary:
        'リスク判断と対人信頼の両面でバランスがとれており、多様な環境や役割に適応しやすい汎用性の高い類型です。特定の環境に強く依存せず、幅広いポジションで安定したパフォーマンスを発揮できます。',
      traits: [
        '状況に応じてリスクと安全性のバランスを自然に調整する',
        '相手の行動パターンを観察しながら信頼度を調整する柔軟性がある',
        '多様なチームや文化にフィットしやすい適応力を持つ',
        '突出した強みよりも安定した総合力で成果を出す',
      ],
      suitableRoles: ['プロジェクトメンバー・汎用職種全般', 'マネジャー補佐・チームサポート職', 'クロスファンクショナルな役割'],
      managementNote:
        '多くの役割に適応できる強みを持ちますが、特定のコアスキルや専門性を育てるキャリア設計が長期的な活躍に有効です。得意分野が明確になると、バランスの良さが更なる強みに転換されます。',
    },
    low: {
      name: '慎重自律型',
      catch: '独立した思考で丁寧に課題を解くアナリスト',
      summary:
        '自律的な判断と慎重なリスク評価を組み合わせるスタイルで、独立した環境で高い精度の成果を出す傾向があります。分析や検証が求められる役割で力を発揮しやすい類型です。',
      traits: [
        '証拠や根拠を重視し、慎重な意思決定を行う分析志向がある',
        '独立した判断と検証を好み、高い精度での成果物を志向する',
        '自律的な作業スタイルを持ち、外部からの干渉を最小化する傾向がある',
        '長期的・論理的な視点で課題にアプローチする',
      ],
      suitableRoles: ['財務・経理・コンプライアンス', '品質管理・リスク管理', 'データアナリスト・リサーチャー'],
      managementNote:
        '専門的な判断と精度の高い成果が強みです。チームへの積極的な関与を促すため、定期的な情報共有の機会や、協業の意味を具体的に示す場を設けることが効果的です。',
    },
  },
  low: {
    high: {
      name: '安定支援型',
      catch: '信頼と誠実さを武器に、組織の土台を支える',
      summary:
        '他者への信頼が高く、リスクを慎重に見極める安定志向のスタイルを持ちます。組織の安心感やチームのまとまりを醸成し、長期にわたって信頼される存在になりやすい類型です。',
      traits: [
        '高い誠実さと信頼性で、周囲から安心して頼られる存在になる',
        'リスクを慎重に評価してから行動し、ミスや失敗を最小化する',
        '継続的な関係構築と丁寧なフォローアップを得意とする',
        '組織やチームの安定に貢献し、長期的な信頼関係を築く',
      ],
      suitableRoles: ['カスタマーサポート・ヘルプデスク', '教育・研修・オンボーディング担当', '管理業務・オフィスマネジメント'],
      managementNote:
        '安定性と誠実さが組織の土台として機能します。変化が大きい環境では、変化の意図を丁寧に説明し、安全に試みられる経験を積む機会を設けることでパフォーマンスが向上します。',
    },
    mid: {
      name: '堅実実行型',
      catch: '着実に仕事を積み上げる、組織に欠かせない実行者',
      summary:
        'リスクを慎重に管理しながら確実な成果を出すスタイルで、プロセスの遵守と継続的な品質維持を得意とします。長期的に安定した成果を求められる環境で力を発揮しやすい類型です。',
      traits: [
        '定められたプロセスを忠実に遂行し、品質と一貫性を確保する',
        '慎重な判断で失敗リスクを低減し、確実な成果を積み上げる',
        '長期的な視点で取り組み、信頼性の高い実績を継続する',
        '安定的な環境で高い集中力を維持し、高精度な成果物を出す',
      ],
      suitableRoles: ['バックオフィス・オペレーション全般', '専門職・職人的技術職', '経営管理・財務・法務サポート'],
      managementNote:
        '実行力と品質の一貫性が強みです。新しいチャレンジや昇格については、段階的なステップアップ設計と十分なサポートを用意することで、スムーズな役割拡張が可能です。',
    },
    low: {
      name: '独立専門型',
      catch: '専門知識を基盤に、独立した判断で確実に動く',
      summary:
        '高い専門性と自律的な判断を組み合わせ、外部依存を最小化した独立したスタイルで成果を出す類型です。深い専門知識が求められるポジションで最も力を発揮しやすい傾向があります。',
      traits: [
        '専門分野への深い集中力と独立した判断力を兼ね備える',
        '外部の承認に依存せず、自己基準で高い品質を追求する',
        '慎重かつ根拠に基づいた意思決定を行い、確実性を優先する',
        '長期的な専門性の蓄積でキャリアを構築するスタイルを持つ',
      ],
      suitableRoles: ['エンジニア・技術職（深い専門領域）', '研究職・サイエンティスト', '士業・資格専門職（法律・会計等）'],
      managementNote:
        '専門性の深さと独立判断力が最大の強みです。協業が求められるシーンでは、チーム内での役割と情報共有の方法を明確に設計することで、専門性を組織の成果に繋げやすくなります。',
    },
  },
};

const RISK_DETAIL: Record<ThreeKey, string> = {
  high: '「リスク志向（BART スコア）」は上位 40% 以内にあり、不確実な状況でも積極的に行動する傾向が確認されました。Lejuez ら（2002）の研究では、BART の高スコアは新奇探索や目標達成行動と正の関連を示しており（r=.15〜.35）、変化の多い環境や新規事業での行動力の指標として参照できます。',
  mid: '「リスク志向（BART スコア）」は中位帯にあり、リスクと安全性のバランスを状況に応じて調整する柔軟な判断スタイルが示されました。過度なリスクテイクも過剰な回避もなく、多様な職場環境に適応しやすい特性です。',
  low: '「リスク志向（BART スコア）」は下位 40% にあり、リスクを慎重に見極めてから行動する堅実なスタイルが確認されました。Lejuez ら（2002）の研究では、低スコアは衝動的なリスクテイクの少なさと関連しており、正確性・確実性が求められる環境での強みとなります。',
};

const TRUST_DETAIL: Record<ThreeKey, string> = {
  high: '「信頼形成（Trust Game スコア）」は上位 40% 以内にあり、相手への信頼に基づいて積極的に協力行動をとる対人スタイルが示されました。Berg ら（1995）の研究では、高い委託率は協調性・互恵志向と関連しており、チームワークや顧客関係の構築において強みを発揮しやすい特性です。',
  mid: '「信頼形成（Trust Game スコア）」は中位帯にあり、相手の行動パターンを確認しながら信頼を調整する柔軟な対人スタイルが確認されました。過度な信頼も過度な警戒もない中庸な姿勢は、様々なタイプの人間関係に適応しやすい特性です。',
  low: '「信頼形成（Trust Game スコア）」は下位 40% にあり、相手を慎重に見極めながら段階的に信頼関係を築くスタイルが示されました。Berg ら（1995）の研究では、低委託率は判断の慎重さと自律性の高さを示す場合があり、独立した専門職や精度重視の役割での強みとなります。',
};

const ADAPT_NOTE: Record<'high' | 'mid' | 'low' | 'null', string> = {
  high: '【適応速度（高）】裏切りなどの負の経験から素早く学習し、投資行動を調整する高い適応力が確認されました。環境変化の大きなポジションや、試行錯誤の多い業務での活躍が特に期待できます。',
  mid: '【適応速度（中）】経験から適切なペースで学習し、行動を調整できる安定した適応力が示されました。急激な変化よりも、段階的な環境変化に対して着実に適応するスタイルです。',
  low: '【適応速度（低）】一貫した行動スタイルを維持する傾向があり、変化への適応には一定の時間を要する可能性があります。安定した環境でその特性が強みになる一方、変化が大きい場面ではサポート体制の整備が推奨されます。',
  null: '【適応速度（測定不能）】今回のアセスメントでは適応速度の算出に必要なイベント数が得られませんでした。この観点は別途評価（面接・参考者照会等）での補完を推奨します。',
};

const DISCLAIMER = `【ご利用上の注意】

本レポートは、行動経済学的アセスメント（BART: Lejuez et al., 2002; Trust Game: Berg et al., 1995）に基づく傾向分析です。採用・昇進・配置の判断を単独で行う根拠としてご利用いただくことはできません。面接・職務経歴・参照者照会などの多面的な評価と組み合わせて活用してください。

また、本ツールの使用にあたっては、NYC Local Law 144（自動化雇用意思決定ツール規制）・EU AI Act・経産省採用AIガイドライン等の採用AI規制を遵守してください。パーセンタイル参照値は暫定値（正規分布近似）であり、Phase 1 の実データ収集後に更新されます。

© kizashi — 心理師監修 認知・行動アセスメント β`;

export function generateReport(input: ReportInput): ReportOutput {
  const hasRisk = input.riskPct !== null;
  const hasTrust = input.trustPct !== null;

  const riskKey: ThreeKey = hasRisk ? toThreeKey(input.riskPct!) : 'mid';
  const trustKey: ThreeKey = hasTrust ? toThreeKey(input.trustPct!) : 'mid';

  const profile = PROFILES[riskKey][trustKey];

  const adaptKey: 'high' | 'mid' | 'low' | 'null' =
    input.adaptPct === null ? 'null' : toThreeKey(input.adaptPct) === 'high' ? 'high' : toThreeKey(input.adaptPct) === 'mid' ? 'mid' : 'low';

  const notePlayed: string[] = [];
  if (!hasRisk) notePlayed.push('リスク志向（G1未受検）');
  if (!hasTrust) notePlayed.push('信頼形成・適応速度（G2未受検）');

  const playNote = notePlayed.length > 0
    ? `\n\n※ ${notePlayed.join('、')}のデータが未取得のため、当該軸は中位値で代替しています。全ゲーム受検後に再評価を推奨します。`
    : '';

  return {
    profileName: profile.name,
    profileCatch: profile.catch,
    summary: profile.summary + playNote,
    traits: profile.traits,
    suitableRoles: profile.suitableRoles,
    managementNote: profile.managementNote,
    adaptNote: ADAPT_NOTE[adaptKey],
    riskDetail: hasRisk ? RISK_DETAIL[riskKey] : '（G1 未受検のためデータなし）',
    trustDetail: hasTrust ? TRUST_DETAIL[trustKey] : '（G2 未受検のためデータなし）',
    disclaimer: DISCLAIMER,
  };
}
